import { Filter, SearchInput, SelectInput, useGetList, useNavigate } from 'react-admin';
import { useMediaQuery } from '@mui/material';
import type { ChipColor, ProgressColor } from '@/types/chip';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Avatar, Chip, TablePagination, Button } from '@mui/material';
import { Person, PersonAdd } from '@mui/icons-material';
import { useState } from 'react';
import { ListPageHeader } from '@/components/ListPageHeader';
import { WizardListToolbar } from '@/components/workflow/WizardListToolbar';
import { EmployeeRecord, STATUS_COLORS, CONTRACT_TYPES, EMPLOYMENT_STATUS } from './constants';

export const EmployeeFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search by name or employee #" />
    <SelectInput source="department" choices={['Finance', 'IT', 'HR', 'Operations', 'Sales', 'Marketing', 'Legal', 'Compliance'].map(d => ({ id: d, name: d }))} />
    <SelectInput source="status" choices={EMPLOYMENT_STATUS.map(s => ({ id: s, name: s }))} alwaysOn />
    <SelectInput source="contractType" choices={CONTRACT_TYPES.map(c => ({ id: c, name: c }))} />
  </Filter>
);

export const EmployeeList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: allEmployees, isLoading } = useGetList('employees', { pagination: { page: 1, perPage: 1000 } });
  const isMobile = useMediaQuery((theme: { breakpoints: { down: (key: string) => string } }) => theme.breakpoints.down('md'));

  const employees = allEmployees?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const stats = {
    total: allEmployees?.length ?? 0,
    active: allEmployees?.filter((e: EmployeeRecord) => e.status === 'Active').length ?? 0,
    onProbation: allEmployees?.filter((e: EmployeeRecord) => e.status === 'OnProbation').length ?? 0,
    suspended: allEmployees?.filter((e: EmployeeRecord) => e.status === 'Suspended').length ?? 0,
    terminated: allEmployees?.filter((e: EmployeeRecord) => e.status === 'Terminated').length ?? 0,
  };

  const occupationalLevels = [
    { key: 'TopManagement', label: 'Top Mgmt' },
    { key: 'SeniorManagement', label: 'Sen Mgmt' },
    { key: 'ProfessionallyQualified', label: 'Professional' },
    { key: 'SkilledTechnical', label: 'Skilled' },
    { key: 'SemiSkilled', label: 'Semi-Skilled' },
    { key: 'Unskilled', label: 'Unskilled' },
  ];
  const levelCounts = occupationalLevels.map(({ key }) =>
    allEmployees?.filter((e: EmployeeRecord) => e.seniority?.occupationalLevel === key).length ?? 0
  );

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <WizardListToolbar domainId="hr.onboarding" wizardPath="wizard/onboarding" label="Onboarding Wizard" />
        </Box>
        <ListPageHeader
          title="Employees"
          metrics={[
            { label: 'Active', value: String(stats.active), color: 'success.main' },
            { label: 'Probation', value: String(stats.onProbation), color: 'warning.main' },
            { label: 'Suspended', value: String(stats.suspended), color: 'error.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {employees?.map((emp: EmployeeRecord) => (
            <Paper key={emp.id} sx={{ p: 2 }} onClick={() => { navigate(`/employees/${emp.id}/show`); }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontSize: 14 }}>
                    {emp.legalEntity?.firstName?.[0]}{emp.legalEntity?.lastName?.[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{emp.legalEntity?.firstName} {emp.legalEntity?.lastName}</Typography>
                    <Typography variant="caption" color="text.secondary">{emp.employeeNumber} · {emp.seniority?.jobTitle}</Typography>
                  </Box>
                </Box>
                <Chip label={emp.status} size="small" color={STATUS_COLORS[emp.status ?? ''] as ChipColor} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography variant="caption" color="text.secondary">{emp.organisationalHierarchy?.department}</Typography>
                <Typography variant="caption" color="text.secondary">·</Typography>
                <Typography variant="caption" color="text.secondary">{emp.contractType?.replace(/([A-Z])/g, ' $1').trim()}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <WizardListToolbar domainId="hr.onboarding" wizardPath="wizard/onboarding" label="Onboarding Wizard" />
      </Box>
      <ListPageHeader
        title="Employees"
        metrics={[
          { label: 'Active', value: String(stats.active), color: 'success.main' },
          { label: 'On Probation', value: String(stats.onProbation), color: 'warning.main' },
          { label: 'Suspended', value: String(stats.suspended), color: 'error.main' },
          { label: 'Terminated', value: String(stats.terminated), color: 'grey.600' },
        ]}
        barChartData={levelCounts}
        barChartLabels={occupationalLevels.map(l => l.label)}
        barChartTitle="By Occupational Level"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Position</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Contract</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees?.map((emp: EmployeeRecord) => (
              <TableRow
                key={emp.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => { navigate(`/employees/${emp.id}/show`); }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 12 }}>
                      {emp.legalEntity?.firstName?.[0]}{emp.legalEntity?.lastName?.[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{emp.legalEntity?.firstName} {emp.legalEntity?.lastName}</Typography>
                      <Typography variant="caption" color="text.secondary">{emp.employeeNumber}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{emp.organisationalHierarchy?.department}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{emp.seniority?.jobTitle}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={emp.status} size="small" color={STATUS_COLORS[emp.status ?? ''] as ChipColor} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {emp.contractType?.replace(/([A-Z])/g, ' $1').trim() || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{emp.employmentTerms?.startDate?.split('T')[0]}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={allEmployees?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!employees || employees.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Person sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No employees found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
